import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, CheckCircle, Wifi, WifiOff } from 'lucide-react';
import CustomLoader from '../../../../shared/components/ui/CustomLoader';
import { useRealTimeChat } from '../../../chat/context/RealTimeChatContext';

/** "EXTRACTION_STARTED" -> "Extraction Started" */
const humanizeStep = (value) =>
    String(value || '')
        .replace(/[_-]+/g, ' ')
        .trim()
        .toLowerCase()
        .replace(/(^|\s)(\S)/g, (_, space, ch) => space + ch.toUpperCase());

const CONNECTION_UI = {
    connected: { label: 'Live updates connected', cls: 'connected', Icon: Wifi },
    connecting: { label: 'Connecting to live updates…', cls: 'connecting', Icon: Wifi },
    disconnected: { label: 'Live updates not connected', cls: 'disconnected', Icon: WifiOff },
};

// Captions that rotate under the "initializing" row. They describe what the analysis does;
// they are not server status messages.
const WAITING_CAPTIONS = [
    'Reading your documents',
    'Extracting requirements, deadlines and deliverables',
    'Preparing the Go / No-Go assessment',
];

const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Smoothly counts a number up/down to `target`. */
const useAnimatedNumber = (target, duration = 600) => {
    const [display, setDisplay] = useState(target);
    const fromRef = useRef(target);

    useEffect(() => {
        if (prefersReducedMotion()) {
            setDisplay(target);
            fromRef.current = target;
            return undefined;
        }
        const from = fromRef.current;
        if (from === target) return undefined;
        const start = performance.now();
        let frame;
        const tick = (now) => {
            const t = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - t, 3); // ease-out
            const value = from + (target - from) * eased;
            fromRef.current = value;
            setDisplay(value);
            if (t < 1) frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [target, duration]);

    return Math.round(display);
};

/**
 * Reveals `text` letter by letter while `active`; finished text is shown in full.
 * The full text is kept (invisible) in the layout so the row doesn't jump while typing.
 */
const TypewriterText = ({ text, active, speed = 14 }) => {
    const value = String(text ?? '');
    const [count, setCount] = useState(active ? 0 : value.length);

    useEffect(() => {
        if (!active || prefersReducedMotion()) {
            setCount(value.length);
            return undefined;
        }
        setCount(0);
        let i = 0;
        const id = setInterval(() => {
            i += 1;
            setCount(i);
            if (i >= value.length) clearInterval(id);
        }, speed);
        return () => clearInterval(id);
    }, [value, active, speed]);

    return (
        <span className="analysis-tw">
            <span className="analysis-sr-only">{value}</span>
            <span className="analysis-tw-ghost" aria-hidden="true">{value}</span>
            <span className="analysis-tw-live" aria-hidden="true">{value.slice(0, count)}</span>
        </span>
    );
};

/** Caption that cross-fades to the next one every few seconds. */
const RotatingCaption = ({ items, interval = 2800 }) => {
    const [index, setIndex] = useState(0);
    useEffect(() => {
        if (items.length < 2) return undefined;
        const id = setInterval(() => setIndex((i) => (i + 1) % items.length), interval);
        return () => clearInterval(id);
    }, [items, interval]);
    return <span key={index} className="analysis-caption">{items[index]}</span>;
};

/**
 * Live progress of the AI document analysis.
 * `steps` are built from the websocket progress messages: { id, title, step, progress, isDone, at }.
 */
const AnalysisProgress = ({
    fetchingGoNoGo,
    goNoGoData,
    filesCount,
    analysisProgress,
    steps = []
}) => {
    const { connectionStatus } = useRealTimeChat();
    const connection = CONNECTION_UI[connectionStatus] || CONNECTION_UI.disconnected;
    const ConnectionIcon = connection.Icon;

    // Keep the newest update in view as messages arrive.
    const listEndRef = useRef(null);
    useEffect(() => {
        if (listEndRef.current && typeof listEndRef.current.scrollIntoView === 'function') {
            listEndRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }, [steps.length]);

    const percent = Math.max(0, Math.min(100, Number(analysisProgress) || 0));
    const shownPercent = useAnimatedNumber(percent);

    return (
        <>
            <div className="scanning-header">
                <div className="scanning-title-box">
                    <div className="scanning-icon-bg analysis-icon-pulse"><Sparkles size={24} /></div>
                    <div className="scanning-text">
                        <h2>{goNoGoData ? 'Go / No-Go Decision' : fetchingGoNoGo ? 'Generating Decision...' : 'AI Document Analysis in Progress'}</h2>
                        {!goNoGoData && <p>Processing {filesCount} document{filesCount !== 1 ? 's' : ''}</p>}
                    </div>
                </div>
                {!fetchingGoNoGo && !goNoGoData && <div className="scanning-percent">{shownPercent}%</div>}
            </div>

            {!fetchingGoNoGo && !goNoGoData && (
                <>
                    <div
                        className="progress-bar-wrapper"
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={percent}
                        aria-label="Analysis progress"
                    >
                        <div className="progress-bar-fill analysis-bar-fill" style={{ width: `${percent}%` }}></div>
                    </div>

                    <div className="analysis-live-bar">
                        <span className="analysis-live-title">Live activity</span>
                        <span className={`analysis-live-pill ${connection.cls}`} title={connection.label}>
                            <span className="analysis-live-dot" />
                            <ConnectionIcon size={13} />
                            {connection.label}
                        </span>
                    </div>

                    {connectionStatus === 'disconnected' && (
                        <div className="analysis-live-note" role="status">
                            Live updates are not connected, so progress may not appear here until the connection is restored.
                        </div>
                    )}

                    <ol className="analysis-timeline" aria-live="polite" aria-label="Analysis activity">
                        {steps.length === 0 ? (
                            <li className="analysis-timeline-item active">
                                <div className="analysis-timeline-icon">
                                    <CustomLoader size={18} className="loader-spin" />
                                </div>
                                <div className="analysis-timeline-body">
                                    <div className="analysis-timeline-title">
                                        <span className="analysis-shimmer-text">Initializing document analysis</span>
                                        <span className="analysis-dots" aria-hidden="true"><i>.</i><i>.</i><i>.</i></span>
                                    </div>
                                    <div className="analysis-timeline-meta">
                                        <RotatingCaption items={WAITING_CAPTIONS} />
                                    </div>
                                </div>
                            </li>
                        ) : (
                            steps.map((step, idx) => {
                                const isDone = Boolean(step.isDone);
                                const isActive = !isDone && idx === steps.length - 1;
                                const state = isDone ? 'done' : isActive ? 'active' : 'pending';
                                const stepLabel = humanizeStep(step.step);
                                const showLabel = stepLabel && stepLabel.toLowerCase() !== String(step.title || '').toLowerCase();
                                const hasProgress = typeof step.progress === 'number' && !Number.isNaN(step.progress);

                                return (
                                    <li key={step.id || idx} className={`analysis-timeline-item ${state}`}>
                                        <div className="analysis-timeline-icon">
                                            {isDone
                                                ? <CheckCircle size={18} className="success-check analysis-check-pop" />
                                                : <CustomLoader size={18} className="loader-spin" />}
                                        </div>
                                        <div className="analysis-timeline-body">
                                            <div className="analysis-timeline-title">
                                                <TypewriterText text={step.title} active={isActive} />
                                            </div>
                                            {(showLabel || step.desc) && (
                                                <div className="analysis-timeline-meta">
                                                    {showLabel && <span className="analysis-timeline-tag">{stepLabel}</span>}
                                                    {step.desc && <span>{step.desc}</span>}
                                                </div>
                                            )}
                                        </div>
                                        {hasProgress && (
                                            <span className="analysis-timeline-percent">{Math.round(step.progress)}%</span>
                                        )}
                                    </li>
                                );
                            })
                        )}
                        <li ref={listEndRef} aria-hidden="true" style={{ listStyle: 'none', height: 0 }} />
                    </ol>
                </>
            )}

            {fetchingGoNoGo && (
                <div className="decision-loading-wrapper">
                    <div className="decision-loader-card">
                        <CustomLoader size={48} style={{ margin: '0 auto' }} />
                        <div className="decision-loader-text-stack">
                            <p className="loading-headline analysis-shimmer-text">Analyzing Document & Requirements</p>
                            <p className="loading-subtext">Evaluating match criteria for Go / No-Go decision...</p>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AnalysisProgress;
